<template>
    <v-autocomplete
        v-model="selection"
        :items="resolvedItems"
        :item-text="text"
        :item-value="value"
        :label="label"
        :rules="rules"
        :prepend-inner-icon="icon"
        :disabled="disabled"
        :readonly="readOnly"
        :clearable="!readOnly"
        :open-on-clear="false"
        :loading="lazyLoading"
        no-filter
        :search-input="lazySearch"
        class="merus-select"
        @change="onChange"
        @click:clear="onClear"
        @focus="onReadOnlyFocus"
        @click="onReadOnlyClick"
        @update:search-input="onSearchInput"
    >
        <template v-slot:append-item>
            <v-list-item
                v-if="lazyHasMore || lazyLoadingMore"
                v-intersect="onLazyEndIntersect"
                dense
                class="merus-select__more"
                @mousedown.prevent
                @click="loadMoreLazyItems"
            >
                <v-list-item-content>
                    <v-list-item-subtitle>
                        <v-progress-circular v-if="lazyLoadingMore" indeterminate size="14" width="2" class="mr-2"></v-progress-circular>
                        {{ lazyFooterLabel }}
                    </v-list-item-subtitle>
                </v-list-item-content>
            </v-list-item>
        </template>
    </v-autocomplete>
</template>

<script>
import Intersect from 'vuetify/lib/directives/intersect';
import lazySelectMixin from './lazySelectMixin';
import './merusSelect.css';

export default {
    name: 'LazySelect',
    directives: { Intersect },
    mixins: [lazySelectMixin],
    props: {
        getUrl: {
            type: String,
            required: true,
        },
        text: {
            type: [String, Function],
            required: true,
        },
        value: {
            type: String,
            required: true,
        },
        icon: {
            type: String,
            required: false,
            default: null,
        },
        label: {
            type: String,
            required: false,
            default() {
                return this.$t('system.choose');
            },
        },
        rules: {
            type: Array,
            required: false,
            default() {
                return [];
            },
        },
        disabled: {
            type: Boolean,
            required: false,
            default: false,
        },
        readOnly: {
            type: Boolean,
            required: false,
            default: false,
        },
        /**
         * Static extras prepended to lazy results (e.g. "Sem Unidade").
         * Kept when selected or when the search matches their label.
         */
        extraItems: {
            type: Array,
            default: () => [],
            validator(val) {
                return Array.isArray(val);
            },
        },
        /** Optional client-side filter applied to each lazy page (e.g. id_empresa). */
        itemFilter: {
            type: Function,
            default: null,
        },
    },
    data: () => ({
        items: [],
        /** Páginas já buscadas na pesquisa atual, antes do filtro/merge. */
        fetchedItems: [],
        selection: null,
        lockedData: null,
        suppressSelectionEmit: false,
    }),
    computed: {
        lazyFooterLabel() {
            if (this.lazyLoadingMore) return this.$t('system.lazyLoadingMore');
            if (this.lazyTotal != null) {
                return this.$t('system.lazyShowingOf', {
                    loaded: this.lazyLoadedCount,
                    total: this.lazyTotal,
                });
            }
            return this.$t('system.lazyLoadMore');
        },
        resolvedItems() {
            const lazy = Array.isArray(this.items) ? this.items : [];
            const extras = Array.isArray(this.extraItems) ? this.extraItems : [];
            if (!extras.length) return lazy;

            const key = this.value;
            const textKey = this.text;
            const selected = this.selection;
            const search = (this.lazySearch || '').trim().toLowerCase();
            const seen = new Set(
                lazy
                    .filter((i) => i != null && i[key] != null && i[key] !== '')
                    .map((i) => String(i[key]))
            );

            const itemLabel = (item) => {
                if (item == null) return '';
                if (typeof textKey === 'function') {
                    try {
                        return String(textKey(item) || '');
                    } catch {
                        return '';
                    }
                }
                return String(item[textKey] != null ? item[textKey] : '');
            };

            const prepend = extras.filter((item) => {
                if (item == null || item[key] == null || item[key] === '') return false;
                if (seen.has(String(item[key]))) return false;
                if (selected != null && selected !== '' && String(selected) === String(item[key])) {
                    return true;
                }
                if (!search) return true;
                return itemLabel(item).toLowerCase().includes(search);
            });

            return prepend.length ? [...prepend, ...lazy] : lazy;
        },
    },
    watch: {
        selection(val) {
            if (this.readOnly) {
                if (val !== this.lockedData) {
                    this.$nextTick(() => {
                        this.selection = this.lockedData;
                    });
                }
                return;
            }
            if (val != null && val !== '') {
                const item = this.resolvedItems.find(
                    (i) => i != null && String(i[this.value]) === String(val)
                );
                if (item) {
                    this.cacheSelectedItems([item], this.value);
                    if (!this.suppressSelectionEmit) {
                        this.$emit('select', item);
                    }
                }
            } else if (!this.suppressSelectionEmit) {
                this.$emit('select', null);
            }
            if (!this.suppressSelectionEmit) {
                this.$emit('input', val);
            }
        },
    },
    mounted() {
        this.loadLazyItems(this.lazySearch);
    },
    methods: {
        onSearchInput(val) {
            this.loadLazyItems(val);
        },
        loadLazyItems(val) {
            this.fetchLazyItems(val, {
                onSuccess: (fetched) => {
                    this.fetchedItems = fetched;
                    this.refreshLazyItems();
                },
            });
        },
        loadMoreLazyItems() {
            this.fetchMoreLazyItems({
                onSuccess: (fetched) => {
                    const before = this.items.length;
                    this.fetchedItems = this.appendUniqueLazyItems(this.fetchedItems, fetched, this.value);
                    this.refreshLazyItems();
                    // Página sem itens novos (repetidos ou barrados pelo filtro): o rodapé
                    // segue visível e o intersect não dispara de novo.
                    if (this.items.length === before) {
                        this.$nextTick(() => this.loadMoreLazyItems());
                    }
                },
            });
        },
        refreshLazyItems() {
            const filtered = this.applyLazyItemFilter(this.fetchedItems);
            this.items = this.mergeLazyItems(filtered, this.selection, this.value);
        },
        onChange() {
            if (this.readOnly) {
                this.restoreLockedSelection();
            }
        },
        restoreLockedSelection() {
            this.selection = this.lockedData;
        },
        onClear() {
            if (this.readOnly) {
                this.restoreLockedSelection();
                return;
            }
            this.lockedData = null;
            this.selection = null;
        },
        onReadOnlyFocus(e) {
            if (!this.readOnly || !e || !e.target) return;
            e.target.blur();
        },
        onReadOnlyClick(e) {
            if (!this.readOnly) return;
            if (e) {
                e.stopPropagation();
                if (e.target && typeof e.target.blur === 'function') {
                    e.target.blur();
                }
            }
        },
        withSuppressedEmit(fn) {
            this.suppressSelectionEmit = true;
            try {
                fn();
            } finally {
                this.$nextTick(() => {
                    this.suppressSelectionEmit = false;
                });
            }
        },
        resetData() {
            this.withSuppressedEmit(() => {
                this.lockedData = null;
                this.selection = null;
                this.selectedItemCache = {};
            });
        },
        /**
         * Seleciona um item que pode nao estar na pagina lazy carregada (ex.: ao editar
         * um registro ja gravado): entra na lista exibida e vira a selecao.
         */
        selectOption(item) {
            if (item == null || item[this.value] == null || item[this.value] === '') {
                this.resetData();
                return;
            }
            this.cacheSelectedItems([item], this.value);
            this.items = this.mergeLazyItems(this.items, item[this.value], this.value);
            this.setData(item[this.value]);
        },
        setData(data) {
            try {
                this.withSuppressedEmit(() => {
                    this.lockedData = data;
                    this.selection = data;
                });
            } catch {
                this.$emit('error', this.$t('system.messages.notPossibleUpdate'));
            }
        },
        setSelectedItem(item) {
            try {
                this.lockedData = item;
                this.selection = item;
                if (!this.readOnly) {
                    this.$emit('input', this.selection);
                }
            } catch {
                this.$emit('error', this.$t('system.messages.notPossibleUpdate'));
            }
        },
    },
};
</script>
